/**
 * RuleBuilder.jsx — recursive AND/OR rule editor for auto-tagging AND
 * notification-rule conditions.
 *
 * Edits a rule Group: { enabled, match: 'all'|'any', rules: [Condition|Group|
 * Aggregate|FileCount] }. Conditions = { field, op, value }. Fully nestable
 * groups. Field/op pickers are driven from the allowlist mirror
 * (utils/ruleFields.js); the server re-validates against the authoritative
 * allowlist on save, so this UI is convenience only.
 *
 * Controlled component: `value` is the rule object (or null), `onChange(next)`
 * gets the updated rule. Callers narrow the pickers per context:
 *   fields         — leaf-field descriptors ({ key, label, kind }); defaults to
 *                    TAG_RULE_FIELDS (the pre-v2 tag picker) so the tag editor
 *                    is unaffected. The notification wizard passes
 *                    fieldsForRuleKind(kind, triggers).
 *   allowAggregate — offer "+ Count condition" (cross-case people counts).
 *                    Default true (tag rules); notification rules pass false
 *                    (the server bans aggregate nodes there).
 *   allowFileCount — offer "+ File count" (this case's attachments per typed
 *                    upload slot). Default false; notification rules pass true.
 *
 * All user values render through inputs / React text — never
 * dangerouslySetInnerHTML — so a poisoned taxonomy value can't inject.
 */

import React, { useState, useContext, useMemo, createContext } from 'react';
import {
  RULE_FIELDS, TAG_RULE_FIELDS, AGGREGATE_FIELDS, COUNT_OPERATORS, AGGREGATE_WINDOWS,
  FILE_COUNT_CATEGORIES,
  isMultiOp, isNumericOp, fieldKind, isTemporalField, operatorsForField, conditionForField,
  suggestionsForField,
  emptyRule, emptyAggregate, emptyFileCount, emptyGroup, isGroup, isAggregate, isFileCount,
  addMultiValue, removeMultiValue, parseMultiValueInput, describeNode,
} from '../utils/ruleFields.js';

// Known values per rule field (e.g. { treatment: ['LHR','AWI'], severity: [...] }),
// supplied by the caller so the value control can be a dropdown of real options
// instead of free text. Empty = free text everywhere (back-compat).
const FieldValuesContext = createContext({});
// Per-builder picker options ({ fields, allowAggregate, allowFileCount }) so the
// recursive editors don't thread three props through every level.
const BuilderOptionsContext = createContext({
  fields: TAG_RULE_FIELDS, allowAggregate: true, allowFileCount: false,
});
// A stable id for <datalist> wiring per field.
let _dlSeq = 0;

// Subtle, grey, live human-readable summary rendered under each leaf/aggregate/
// fileCount row — e.g. "Days since reported is more than 90". Updates as the row
// is edited. Suppressed while the node is still incomplete so it never shows a
// half-typed "… is more than" line.
function NodeSummary({ node }) {
  if (!nodeIsComplete(node)) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic', paddingLeft: 2 }}>
      {describeNode(node)}
    </div>
  );
}

// A node is "complete enough" to summarise: a leaf needs a usable value; an
// aggregate/fileCount needs a numeric count value. (Field/op always have a
// select default.)
function nodeIsComplete(node) {
  if (!node || typeof node !== 'object') return false;
  if (isAggregate(node) || isFileCount(node)) {
    return typeof node.value === 'number' && Number.isFinite(node.value);
  }
  if (Array.isArray(node.value)) return node.value.length > 0;
  if (isNumericOp(node.op)) return typeof node.value === 'number' && Number.isFinite(node.value);
  return String(node.value ?? '').trim() !== '';
}

export function MultiValueInput({ values, onChange, placeholder, suggestions }) {
  const [draft, setDraft] = useState('');
  const [dlId] = useState(() => `rbdl-${++_dlSeq}`);
  const list = Array.isArray(values) ? values : [];
  const opts = Array.isArray(suggestions) ? suggestions.filter(s => !list.includes(s)) : [];

  function commitDraft(raw) {
    const parts = parseMultiValueInput(raw);
    if (!parts.length) return;
    let next = list;
    for (const p of parts) next = addMultiValue(next, p);
    onChange(next);
    setDraft('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitDraft(draft);
    } else if (e.key === 'Backspace' && !draft && list.length) {
      onChange(removeMultiValue(list, list.length - 1));
    }
  }

  return (
    <div className="rule-multi-value" style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4,
      flex: 1, minWidth: 140, padding: '3px 6px', fontSize: 12,
      border: '1px solid var(--border-strong)', borderRadius: 8,
      background: 'var(--surface)',
    }}>
      {list.map((v, i) => (
        <span key={`${v}-${i}`} className="chip" style={{
          fontSize: 11, padding: '2px 8px', cursor: 'default', pointerEvents: 'auto',
        }}>
          {v}
          <button type="button" aria-label={`Remove ${v}`}
                  onClick={() => onChange(removeMultiValue(list, i))}
                  style={{
                    border: 0, background: 'transparent', cursor: 'pointer',
                    padding: 0, marginLeft: 4, lineHeight: 1, color: 'var(--text-3)',
                    fontSize: 14,
                  }}>×</button>
        </span>
      ))}
      <input type="text" value={draft}
             placeholder={list.length ? 'Add value…' : (placeholder || 'value1, value2…')}
             list={opts.length ? dlId : undefined}
             onChange={e => setDraft(e.target.value)}
             onKeyDown={handleKeyDown}
             onBlur={() => { if (draft.trim()) commitDraft(draft); }}
             style={{
               border: 0, outline: 0, flex: 1, minWidth: 80,
               fontFamily: 'inherit', fontSize: 12, background: 'transparent',
               color: 'var(--text)',
             }}
             aria-label="Multi value" />
      {opts.length > 0 && (
        <datalist id={dlId}>{opts.map(v => <option key={v} value={v} />)}</datalist>
      )}
    </div>
  );
}

function GroupEditor({ group, onChange, onRemove, depth }) {
  const { fields, allowAggregate, allowFileCount } = useContext(BuilderOptionsContext);
  const children = Array.isArray(group.rules) ? group.rules : [];

  function update(patch) { onChange({ ...group, ...patch }); }
  function updateChild(i, next) {
    const rules = children.slice();
    rules[i] = next;
    update({ rules });
  }
  function removeChild(i) {
    update({ rules: children.filter((_, j) => j !== i) });
  }

  const matchWord = group.match === 'any' ? 'OR' : 'AND';
  const matchLabel = group.match === 'any' ? 'ANY (OR)' : 'ALL (AND)';

  return (
    <fieldset className={`rule-group ${depth % 2 ? 'rule-group--alt' : 'rule-group--base'}`}>
      <legend className="rule-group-legend">
        Match {matchLabel} of the conditions below
      </legend>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Match</span>
        <select
          className="input sm"
          value={group.match === 'any' ? 'any' : 'all'}
          onChange={e => update({ match: e.target.value })}
          style={{ fontSize: 12, padding: '2px 6px' }}
          aria-label="Match mode"
        >
          <option value="all">ALL (AND)</option>
          <option value="any">ANY (OR)</option>
        </select>
        <span style={{ flex: 1 }} />
        {onRemove && (
          <button type="button" className="btn ghost sm" onClick={onRemove}
                  style={{ fontSize: 11, color: 'var(--danger)' }}>Remove group</button>
        )}
      </div>

      {children.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-3)', padding: '4px 0 8px' }}>
          No conditions yet — this group matches nothing.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {children.map((child, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            {i > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', width: 30, paddingTop: 7 }}>
                {matchWord}
              </span>
            )}
            {i === 0 && <span style={{ width: 30 }} />}
            <div style={{ flex: 1 }}>
              {isGroup(child)
                ? <GroupEditor group={child} depth={depth + 1}
                               onChange={next => updateChild(i, next)}
                               onRemove={() => removeChild(i)} />
                : isAggregate(child)
                ? <AggregateRow node={child}
                                onChange={next => updateChild(i, next)}
                                onRemove={() => removeChild(i)} />
                : isFileCount(child)
                ? <FileCountRow node={child}
                                onChange={next => updateChild(i, next)}
                                onRemove={() => removeChild(i)} />
                : <ConditionRow cond={child}
                                onChange={next => updateChild(i, next)}
                                onRemove={() => removeChild(i)} />}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn ghost sm" style={{ fontSize: 11 }}
                onClick={() => update({ rules: [...children, conditionForField(fields[0]?.key || 'treatment')] })}>
          + Condition
        </button>
        {allowAggregate && (
          <button type="button" className="btn ghost sm" style={{ fontSize: 11 }}
                  onClick={() => update({ rules: [...children, emptyAggregate()] })}>
            + Count condition
          </button>
        )}
        {allowFileCount && (
          <button type="button" className="btn ghost sm" style={{ fontSize: 11 }}
                  onClick={() => update({ rules: [...children, emptyFileCount()] })}>
            + File count
          </button>
        )}
        {depth < 8 && (
          <button type="button" className="btn ghost sm" style={{ fontSize: 11 }}
                  onClick={() => update({ rules: [...children, emptyGroup()] })}>
            + Group
          </button>
        )}
      </div>
    </fieldset>
  );
}

function ConditionRow({ cond, onChange, onRemove }) {
  const { fields } = useContext(BuilderOptionsContext);
  const numeric  = isNumericOp(cond.op);
  const multi    = isMultiOp(cond.op);
  const temporal = isTemporalField(cond.field);
  const ops      = operatorsForField(cond.field);
  const allFieldValues = useContext(FieldValuesContext);
  const knownValues = suggestionsForField(cond.field, allFieldValues);
  const [dlId] = useState(() => `rbdl-${++_dlSeq}`);

  // A stored rule may reference a field the current picker set doesn't offer
  // (e.g. a previous* field after the change trigger was deselected) — keep it
  // selectable so the row renders honestly instead of blanking the select.
  const fieldOpts = fields.some(f => f.key === cond.field)
    ? fields
    : [...fields, RULE_FIELDS.find(f => f.key === cond.field) || { key: cond.field, label: cond.field }];

  function changeField(nextField) {
    // Switching kind (string<->numeric) must reshape op + value to stay valid.
    if (fieldKind(nextField) !== fieldKind(cond.field)) {
      onChange({ ...conditionForField(nextField) });
    } else {
      onChange({ ...cond, field: nextField });
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <select className="input sm" value={cond.field}
              onChange={e => changeField(e.target.value)}
              style={{ fontSize: 12, padding: '3px 6px' }} aria-label="Field">
        {fieldOpts.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
      <select className="input sm" value={cond.op}
              onChange={e => {
                const nextOp = e.target.value;
                const nextMulti = isMultiOp(nextOp);
                // Convert value shape when switching single<->multi.
                let value = cond.value;
                if (nextMulti && !Array.isArray(value)) value = value ? [String(value)] : [];
                if (!nextMulti && Array.isArray(value)) value = value[0] ?? '';
                onChange({ ...cond, op: nextOp, value });
              }}
              style={{ fontSize: 12, padding: '3px 6px' }} aria-label="Operator">
        {ops.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      {numeric ? (
        <input className="input sm" type="number"
               value={Number.isFinite(cond.value) ? cond.value : ''}
               placeholder={temporal ? 'days' : 'value'}
               onChange={e => {
                 const n = e.target.value === '' ? '' : Number(e.target.value);
                 onChange({ ...cond, value: e.target.value === '' ? '' : (Number.isNaN(n) ? '' : n) });
               }}
               style={{ fontSize: 12, padding: '3px 6px', width: 100 }}
               aria-label={temporal ? 'Days' : 'Value'} />
      ) : multi ? (
        <MultiValueInput values={Array.isArray(cond.value) ? cond.value : []}
                         onChange={next => onChange({ ...cond, value: next })}
                         placeholder="Add values…"
                         suggestions={knownValues} />
      ) : (
        <>
          <input className="input sm" type="text"
                 value={cond.value ?? ''}
                 placeholder={knownValues.length ? 'pick or type…' : 'value'}
                 list={knownValues.length ? dlId : undefined}
                 onChange={e => onChange({ ...cond, value: e.target.value })}
                 style={{ fontSize: 12, padding: '3px 6px', minWidth: 140, flex: 1 }}
                 aria-label="Value" />
          {knownValues.length > 0 && (
            <datalist id={dlId}>{knownValues.map(v => <option key={v} value={v} />)}</datalist>
          )}
        </>
      )}
      {/* "days" caption only for the temporal day-counts — severityLevel and
          other plain numerics are unitless. */}
      {numeric && temporal && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>days</span>}
      <button type="button" className="btn ghost sm" onClick={onRemove}
              title="Remove condition" aria-label="Remove condition"
              style={{ fontSize: 13, color: 'var(--danger)', lineHeight: 1, padding: '2px 8px' }}>×</button>
    </div>
    <NodeSummary node={cond} />
    </div>
  );
}

// AGGREGATE (windowed people-count) editor: "Same staff member concerned · at
// least · 2 · cases · in last 90 days".
function AggregateRow({ node, onChange, onRemove }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
                  padding: '4px 6px', border: '1px dashed var(--border-strong)', borderRadius: 8 }}>
      <select className="input sm" value={node.field}
              onChange={e => onChange({ ...node, field: e.target.value })}
              style={{ fontSize: 12, padding: '3px 6px' }} aria-label="Aggregate field">
        {AGGREGATE_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>on</span>
      <select className="input sm" value={node.op}
              onChange={e => onChange({ ...node, op: e.target.value })}
              style={{ fontSize: 12, padding: '3px 6px' }} aria-label="Count operator">
        {COUNT_OPERATORS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      <input className="input sm" type="number" min="0"
             value={Number.isFinite(node.value) ? node.value : ''}
             onChange={e => {
               const n = Number(e.target.value);
               onChange({ ...node, value: e.target.value === '' ? '' : (Number.isInteger(n) && n >= 0 ? n : node.value) });
             }}
             style={{ fontSize: 12, padding: '3px 6px', width: 64 }}
             aria-label="Count value" />
      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>cases</span>
      <select className="input sm"
              value={node.window === 'all' ? 'all' : String(node.window)}
              onChange={e => onChange({ ...node, window: e.target.value === 'all' ? 'all' : Number(e.target.value) })}
              style={{ fontSize: 12, padding: '3px 6px' }} aria-label="Window">
        {AGGREGATE_WINDOWS.map(w => <option key={String(w.key)} value={String(w.key)}>{w.label}</option>)}
      </select>
      <button type="button" className="btn ghost sm" onClick={onRemove}
              title="Remove count condition" aria-label="Remove count condition"
              style={{ fontSize: 13, color: 'var(--danger)', lineHeight: 1, padding: '2px 8px' }}>×</button>
    </div>
    <NodeSummary node={node} />
    </div>
  );
}

// FILECOUNT (this case's attachments per typed upload slot) editor:
// "Training certificate · at least · 1 · file(s)".
function FileCountRow({ node, onChange, onRemove }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
                  padding: '4px 6px', border: '1px dashed var(--border-strong)', borderRadius: 8 }}>
      <select className="input sm" value={node.category}
              onChange={e => onChange({ ...node, category: e.target.value })}
              style={{ fontSize: 12, padding: '3px 6px' }} aria-label="File category">
        {FILE_COUNT_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
      <select className="input sm" value={node.op}
              onChange={e => onChange({ ...node, op: e.target.value })}
              style={{ fontSize: 12, padding: '3px 6px' }} aria-label="File count operator">
        {COUNT_OPERATORS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      <input className="input sm" type="number" min="0"
             value={Number.isFinite(node.value) ? node.value : ''}
             onChange={e => {
               const n = Number(e.target.value);
               onChange({ ...node, value: e.target.value === '' ? '' : (Number.isInteger(n) && n >= 0 ? n : node.value) });
             }}
             style={{ fontSize: 12, padding: '3px 6px', width: 64 }}
             aria-label="File count value" />
      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>file(s)</span>
      <button type="button" className="btn ghost sm" onClick={onRemove}
              title="Remove file count condition" aria-label="Remove file count condition"
              style={{ fontSize: 13, color: 'var(--danger)', lineHeight: 1, padding: '2px 8px' }}>×</button>
    </div>
    <NodeSummary node={node} />
    </div>
  );
}

export default function RuleBuilder({
  value, onChange, fieldValues, showEnabled = true,
  fields = TAG_RULE_FIELDS, allowAggregate = true, allowFileCount = false,
}) {
  const rule = value && isGroup(value) ? value : emptyRule();
  const options = useMemo(
    () => ({ fields: fields?.length ? fields : TAG_RULE_FIELDS, allowAggregate, allowFileCount }),
    [fields, allowAggregate, allowFileCount],
  );

  return (
    <FieldValuesContext.Provider value={fieldValues || {}}>
    <BuilderOptionsContext.Provider value={options}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {showEnabled && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={rule.enabled !== false}
                   onChange={e => onChange({ ...rule, enabled: e.target.checked })} />
            Rule enabled (auto-applies this tag to matching cases)
          </label>
        )}
        <GroupEditor group={rule} depth={0} onChange={onChange} />
      </div>
    </BuilderOptionsContext.Provider>
    </FieldValuesContext.Provider>
  );
}
